/**
 * Bridge between Three.js types and worker-safe IK types.
 *
 * Converts Three.js IKChainState, SkeletonState, and bone maps into
 * serializable plain-array formats that can be sent to the IK worker.
 */

import * as THREE from "three/webgpu";
import type { IKChainState } from "./ikChainState.ts";
import type {
  IKChainStateCore,
  SkeletonStateCore,
  WholeBodyTargetCore,
} from "./ikTypes.ts";
import type { WholeBodyTarget } from "./wholeBodyIK.ts";
import type { Quat, Vec3 } from "./workerMath.ts";

// ---------------------------------------------------------------------------
// Three.js → Plain array conversions
// ---------------------------------------------------------------------------

export function vec3ToPlain(v: THREE.Vector3): Vec3 {
  return [v.x, v.y, v.z];
}

export function quatToPlain(q: THREE.Quaternion): Quat {
  return [q.x, q.y, q.z, q.w];
}

/**
 * Convert a Three.js IKChainState to the worker-safe IKChainStateCore format.
 */
export function chainStateToCore(chain: IKChainState): IKChainStateCore {
  return {
    positions: chain.positions.map(vec3ToPlain),
    lengths: chain.lengths,
    boneNames: chain.boneNames,
    startLocalQuats: chain.startLocalQuats.map(quatToPlain),
    parentWorldQuats: chain.parentWorldQuats.map(quatToPlain),
  };
}

/**
 * Build a SkeletonStateCore from live Three.js bones.
 * Worker-safe version of buildSkeletonState() from wholeBodyIK.ts.
 */
export function buildSkeletonStateCore(
  boneMap: Map<string, THREE.Bone>,
  currentPoses: ReadonlyMap<string, Quat> | undefined,
  warmStartQuats: ReadonlyMap<string, Quat> | undefined,
): SkeletonStateCore {
  const root = boneMap.get("root");
  if (!root) throw new Error("Skeleton missing root bone");

  const ordered: THREE.Bone[] = [];
  const boneToIndex = new Map<THREE.Bone, number>();
  const queue: THREE.Bone[] = [root];

  while (queue.length > 0) {
    const bone = queue.shift()!;
    if (bone.name.startsWith("finger_")) continue;
    boneToIndex.set(bone, ordered.length);
    ordered.push(bone);
    for (const child of bone.children) {
      if ((child as THREE.Bone).isBone) queue.push(child as THREE.Bone);
    }
  }

  const n = ordered.length;
  const names: string[] = [];
  const parentIdx = new Int32Array(n);
  const localQuats: Quat[] = [];
  const offsets: Vec3[] = [];

  const _q1 = new THREE.Quaternion();
  const _q2 = new THREE.Quaternion();
  const _v1 = new THREE.Vector3();
  const _v2 = new THREE.Vector3();

  for (let i = 0; i < n; i++) {
    const bone = ordered[i];
    names.push(bone.name);

    const parent = bone.parent;
    if (
      parent &&
      (parent as THREE.Bone).isBone &&
      boneToIndex.has(parent as THREE.Bone)
    ) {
      parentIdx[i] = boneToIndex.get(parent as THREE.Bone)!;
    } else {
      parentIdx[i] = -1;
    }

    const ws = warmStartQuats?.get(bone.name);
    const cp = currentPoses?.get(bone.name);
    if (ws) {
      localQuats.push([...ws] as Quat);
    } else if (cp) {
      localQuats.push([...cp] as Quat);
    } else {
      localQuats.push(quatToPlain(bone.quaternion));
    }

    if (parentIdx[i] >= 0) {
      const parentBone = ordered[parentIdx[i]];
      _q1.identity();
      if (parentBone.parent) parentBone.parent.getWorldQuaternion(_q1);
      _q1.multiply(parentBone.quaternion);
      _q2.copy(_q1).invert();
      parentBone.getWorldPosition(_v1);
      bone.getWorldPosition(_v2);
      const diff = _v2.clone().sub(_v1).applyQuaternion(_q2);
      offsets.push(vec3ToPlain(diff));
    } else {
      offsets.push([0, 0, 0]);
    }
  }

  const rootPos = new THREE.Vector3();
  root.getWorldPosition(rootPos);
  const rootParentQuat = new THREE.Quaternion();
  if (root.parent) root.parent.getWorldQuaternion(rootParentQuat);

  return {
    names,
    parentIdx,
    localQuats,
    offsets,
    rootPos: vec3ToPlain(rootPos),
    rootParentQuat: quatToPlain(rootParentQuat),
  };
}

/**
 * Convert Three.js WholeBodyTarget to plain-array format.
 */
export function wholeBodyTargetToCore(
  target: WholeBodyTarget,
): WholeBodyTargetCore {
  return {
    boneName: target.boneName,
    position: vec3ToPlain(target.position),
    weight: target.weight,
  };
}

/**
 * Collect current bone poses as serializable entries.
 * Used to provide the worker with the current skeleton state for corrective weight computation.
 */
export function collectBonePoses(
  bonePoses: Map<string, Quat> | undefined,
  boneMap: Map<string, THREE.Bone>,
  restPoses: Map<string, Quat> | undefined,
): Array<[string, Quat]> {
  const result: Array<[string, Quat]> = [];
  for (const [name, bone] of boneMap) {
    const stored = bonePoses?.get(name);
    if (stored) {
      result.push([name, stored]);
    } else {
      const rest = restPoses?.get(name);
      if (rest) {
        result.push([name, rest]);
      } else {
        result.push([name, quatToPlain(bone.quaternion)]);
      }
    }
  }
  return result;
}
